<template>
    <div
        class="vks-section-drawers"
        :class="{
            'is-rail-pinned': railPinned,
            'has-open': Boolean(openSection),
        }"
        :style="railPinStyle"
    >
        <div
            v-if="openSection"
            class="vks-section-drawer-backdrop"
            role="presentation"
            @click="$emit('toggle-drawer', openDrawerId)"
        ></div>
        <aside
            class="vks-section-drawer-panel"
            :class="{ 'is-open': Boolean(openSection) }"
            :aria-hidden="openSection ? 'false' : 'true'"
        >
            <template v-if="openSection">
                <header class="vks-section-drawer-head">
                    <h3 class="vks-section-drawer-title">{{ openSection.label }}</h3>
                    <button
                        type="button"
                        class="vks-section-drawer-close"
                        :aria-label="'Close ' + openSection.label"
                        @click="$emit('toggle-drawer', openDrawerId)"
                    >
                        &times;
                    </button>
                </header>
                <div class="vks-section-drawer-body">
                    <VariantSifterAssociationsDrawer
                        v-if="openSection.id === 'associations'"
                        :rows="associationsState.rows"
                        :filters-index="associationsState.filtersIndex"
                        :loading="associationsState.loading"
                        :hide-loading-status="regionLoadProgressActive"
                        :error="associationsState.error"
                        :index-name="associationsState.index"
                        :query-string="associationsState.query"
                        :search-session="searchSession"
                        :utils="utils"
                        :ld-loading="associationsState.ldLoading"
                        :ld-error="associationsState.ldError"
                        :ancestry-bubbles="ancestryBubbles"
                        :ancestry-bubble-groups="ancestryBubbleGroups"
                        :primary-ancestry="primaryAncestry"
                        :selected-ancestries="associationsState.selectedAncestries"
                        :selected-phenotypes="associationsState.selectedPhenotypes"
                        :ancestry-series-loading="associationsState.ancestrySeriesLoading"
                        :ancestry-availability-loading="
                            associationsState.ancestryAvailabilityLoading
                        "
                        :ancestry-availability-error="associationsState.ancestryAvailabilityError"
                        :project-id="projectId"
                        :plot-overlays-state="plotOverlaysState"
                        :plot-markers="plotMarkers"
                        :starred-variant-ids="starredVariantIds"
                        :credible-sets-state="credibleSetsState"
                        :global-enrichment-state="globalEnrichmentState"
                        :v2g-state="v2gState"
                        :s2g-state="s2gState"
                        :mapping-state="mappingState"
                        :workspace-mapping-filter="workspaceMappingFilter"
                        @update:filtersIndex="$emit('update:associationsFiltersIndex', $event)"
                        @toggle-ancestry="$emit('toggle-association-ancestry', $event)"
                        @toggle-star-variant="$emit('toggle-star-variant', $event)"
                        @set-reference-variant="$emit('set-reference-variant', $event)"
                        @update:mappingState="$emit('update:mappingState', $event)"
                    />
                    <VariantSifterCredibleSetsDrawer
                        v-else-if="openSection.id === 'credible-sets'"
                        :credible-sets-state="credibleSetsState"
                        :color-by-set-id="credibleSetPillColors"
                        :search-session="searchSession"
                        :utils="utils"
                        :starred-variant-ids="starredVariantIds"
                        @add-set="$emit('add-credible-set', $event)"
                        @remove-set="$emit('remove-credible-set', $event)"
                        @toggle-star-variant="$emit('toggle-star-variant', $event)"
                        @update:panelFilters="$emit('update:credibleSetsPanelFilters', $event)"
                    />
                    <VariantSifterGenesDrawer
                        v-else-if="openSection.id === 'genes'"
                        :genes-state="genesState"
                        @update:selectedTypes="$emit('update:genesSelectedTypes', $event)"
                    />
                    <VariantSifterGlobalEnrichmentDrawer
                        v-else-if="openSection.id === 'global-enrichment'"
                        :global-enrichment-state="globalEnrichmentState"
                        :search-session="searchSession"
                        :utils="utils"
                        :hide-loading-status="regionLoadProgressActive"
                        @update:enabledMutedAnnotations="
                            $emit('update:geEnabledMutedAnnotations', $event)
                        "
                        @update:enabledMutedAnnotationTissues="
                            $emit('update:geEnabledMutedAnnotationTissues', $event)
                        "
                        @update:disabledAnnotationTissues="
                            $emit('update:geDisabledAnnotationTissues', $event)
                        "
                        @update:selectedAnnotations="$emit('update:geSelectedAnnotations', $event)"
                        @update:tissueTrackSort="$emit('update:geTissueTrackSort', $event)"
                        @update:geTrackPValueMax="$emit('update:geTrackPValueMax', $event)"
                        @update:selectedMethods="$emit('update:geSelectedMethods', $event)"
                        @update:selectedSources="$emit('update:geSelectedSources', $event)"
                    />
                    <VariantSifterV2gDrawer
                        v-else-if="openSection.id === 'variant-to-gene-links'"
                        :v2g-state="v2gState"
                        :global-enrichment-state="globalEnrichmentState"
                        :search-session="searchSession"
                        :view-region="viewRegion"
                        :utils="utils"
                        @update:selectedTissues="$emit('update:v2gSelectedTissues', $event)"
                        @update:deselectedMethods="$emit('update:v2gDeselectedMethods', $event)"
                        @update:deselectedGenes="$emit('update:v2gDeselectedGenes', $event)"
                        @update:deselectedTissues="$emit('update:v2gDeselectedTissues', $event)"
                        @update:deselectedBiosamples="
                            $emit('update:v2gDeselectedBiosamples', $event)
                        "
                    />
                    <VariantSifterS2gDrawer
                        v-else-if="openSection.id === 'snp2gene-links'"
                        :s2g-state="s2gState"
                        :search-session="searchSession"
                        :view-region="viewRegion"
                        :utils="utils"
                        @load="$emit('load-s2g')"
                        @clear="$emit('clear-s2g')"
                        @update:deselectedGenes="$emit('update:s2gDeselectedGenes', $event)"
                    />
                    <template v-else>
                        <p class="vks-section-drawer-note">{{ openSection.description }}</p>
                        <p class="vks-section-drawer-note">
                            Search and filter UI for this section will appear here.
                        </p>
                    </template>
                </div>
            </template>
        </aside>
        <div class="vks-section-drawer-tabs" role="tablist" aria-label="Section controls">
            <button
                v-for="section in sections"
                :key="section.id"
                type="button"
                role="tab"
                class="vks-section-drawer-tab"
                :class="{
                    'is-active': section.id === openDrawerId,
                    'is-attached': section.id === openDrawerId && Boolean(openDrawerId),
                }"
                :style="tabStyle(section)"
                :aria-selected="section.id === openDrawerId ? 'true' : 'false'"
                :title="section.label"
                @click="$emit('toggle-drawer', section.id)"
            >
                <span class="vks-section-drawer-tab-label">{{ drawerTabLabel(section) }}</span>
            </button>
        </div>
    </div>
</template>

<script>
import { drawerTabLabel, drawerTabHeight, sectionById } from "./variantSifterSections.js";
import {
    availableAncestryBubbles,
    buildAncestryBubbleGroups,
    primaryAssociationAncestry,
} from "./variantSifterAssociationsApi.js";
import { VKS_PROJECT_DEFAULT_ID } from "./variantSifterProjects.js";
import VariantSifterAssociationsDrawer from "./VariantSifterAssociationsDrawer.vue";

import VariantSifterCredibleSetsDrawer from "./VariantSifterCredibleSetsDrawer.vue";
import VariantSifterGenesDrawer from "./VariantSifterGenesDrawer.vue";
import VariantSifterGlobalEnrichmentDrawer from "./VariantSifterGlobalEnrichmentDrawer.vue";
import VariantSifterV2gDrawer from "./VariantSifterV2gDrawer.vue";
import VariantSifterS2gDrawer from "./VariantSifterS2gDrawer.vue";

export default {
    name: "VariantSifterSectionDrawers",
    components: {
        VariantSifterAssociationsDrawer,
        VariantSifterCredibleSetsDrawer,
        VariantSifterGenesDrawer,
        VariantSifterGlobalEnrichmentDrawer,
        VariantSifterV2gDrawer,
        VariantSifterS2gDrawer,
    },
    props: {
        sections: {
            type: Array,
            default: () => [],
        },
        openDrawerId: {
            type: String,
            default: null,
        },
        searchSession: {
            type: Object,
            default: null,
        },
        projectId: {
            type: String,
            default: VKS_PROJECT_DEFAULT_ID,
        },
        associationsState: {
            type: Object,
            default: () => ({
                loading: false,
                ldLoading: false,
                error: null,
                ldError: null,
                rows: [],
                index: null,
                query: null,
                filtersIndex: null,
                ancestryAvailability: [],
                ancestryAvailabilityByPhenotype: {},
                ancestryAvailabilityLoading: false,
                ancestryAvailabilityLoadingByPhenotype: {},
                ancestryAvailabilityError: null,
                ancestryAvailabilityErrorByPhenotype: {},
                selectedAncestries: [],
                ancestrySeriesLoading: {},
                selectedPhenotypes: [],
            }),
        },
        plotOverlaysState: {
            type: Object,
            default: () => ({
                ready: false,
                loading: false,
                error: null,
                recombData: null,
                refVariant: null,
            }),
        },
        utils: {
            type: Object,
            default: null,
        },
        plotMarkers: {
            type: Object,
            default: () => ({
                starredVariants: [],
                positionMarkers: [],
            }),
        },
        credibleSetsState: {
            type: Object,
            default: () => ({
                listLoading: false,
                listError: null,
                available: [],
                selectedIds: [],
                variantsBySet: {},
                variantsLoading: false,
                variantsError: null,
            }),
        },
        credibleSetPillColors: {
            type: Object,
            default: () => ({}),
        },
        genesState: {
            type: Object,
            default: () => ({
                ready: false,
                loading: false,
                error: null,
                data: null,
                selectedTypes: ["protein_coding"],
            }),
        },
        globalEnrichmentState: {
            type: Object,
            default: () => ({
                loading: false,
                error: null,
                geRows: [],
                annoRows: [],
                annoData: {},
                catalog: { annotations: [], tissues: [] },
                llmRelevance: {
                    loading: false,
                    error: null,
                    llmUsed: false,
                    relevantAnnotations: [],
                    relevantTissues: [],
                    rationaleById: {},
                },
                enabledMutedAnnotations: [],
                enabledMutedAnnotationTissues: {},
                disabledAnnotationTissues: {},
                tissueTrackSort: "alphabetical",
            }),
        },
        v2gState: {
            type: Object,
            default: () => ({
                tissueData: {},
                selectedTissues: [],
                loadingTissue: null,
                error: null,
                tissueErrors: {},
                deselectedMethods: [],
                deselectedGenes: [],
                deselectedTissues: [],
                deselectedBiosamples: [],
            }),
        },
        s2gState: {
            type: Object,
            default: () => ({
                tissueData: {},
                selectedTissues: [],
                loadingTissue: null,
                error: null,
                tissueErrors: {},
                deselectedMethods: [],
                deselectedGenes: [],
            }),
        },
        mappingState: {
            type: Object,
            default: () => ({
                selectedCategoryIds: [],
                mappingMode: "or",
            }),
        },
        workspaceMappingFilter: {
            type: Object,
            default: null,
        },
        viewRegion: {
            type: Object,
            default: null,
        },
        regionLoadProgressActive: {
            type: Boolean,
            default: false,
        },
        railPinned: {
            type: Boolean,
            default: false,
        },
        railPinStyle: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        starredVariantIds() {
            return (this.plotMarkers?.starredVariants || []).map(
                (entry) => entry.variantId
            );
        },
        openSection() {
            return sectionById(this.openDrawerId);
        },
        primaryAncestry() {
            return primaryAssociationAncestry(this.searchSession);
        },
        ancestryBubbles() {
            return availableAncestryBubbles(
                this.associationsState?.ancestryAvailability,
                this.primaryAncestry
            );
        },
        ancestryBubbleGroups() {
            return buildAncestryBubbleGroups({
                ancestryAvailabilityByPhenotype:
                    this.associationsState?.ancestryAvailabilityByPhenotype || {},
                primaryAncestry: this.primaryAncestry,
                primaryPhenotype: this.searchSession?.phenotype || null,
                selectedPhenotypes: this.associationsState?.selectedPhenotypes || [],
                selectedAncestries: this.associationsState?.selectedAncestries || [],
                ancestrySeriesLoading:
                    this.associationsState?.ancestrySeriesLoading || {},
                ancestryAvailabilityLoadingByPhenotype:
                    this.associationsState?.ancestryAvailabilityLoadingByPhenotype ||
                    {},
                ancestryAvailabilityErrorByPhenotype:
                    this.associationsState?.ancestryAvailabilityErrorByPhenotype ||
                    {},
            });
        },
    },
    methods: {
        drawerTabLabel(section) {
            return drawerTabLabel(section);
        },
        tabStyle(section) {
            return { height: `${drawerTabHeight(section)}px` };
        },
    },
};
</script>

<style scoped>
.vks-section-drawers {
    position: relative;
    flex-shrink: 0;
    width: var(--vks-drawer-tab-width, 30px);
    height: 100%;
    min-height: 240px;
    pointer-events: none;
    z-index: 6;
}

.vks-section-drawers.is-rail-pinned {
    pointer-events: none;
}

.vks-section-drawers.has-open {
    z-index: 30;
}

.vks-section-drawer-backdrop {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: auto;
    background: rgba(24, 26, 32, 0.45);
}

.vks-section-drawer-tabs {
    position: absolute;
    top: 0;
    right: 0;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 12px 0;
    width: var(--vks-drawer-tab-width, 30px);
    z-index: 2;
}

.vks-section-drawer-tab {
    box-sizing: border-box;
    width: var(--vks-drawer-tab-width, 30px);
    min-height: 88px;
    padding: 0;
    border: 1px solid var(--cfde-border, #e6e1d6);
    border-right: none;
    border-radius: 8px 0 0 8px;
    background: #ffffff;
    color: var(--cfde-orange, #e07b39);
    cursor: pointer;
    box-shadow: -3px 0 10px rgba(20, 22, 30, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.22s ease, border-color 0.22s ease, background-color 0.22s ease;
}

.vks-section-drawer-tab.is-attached {
    transform: translateX(
        calc(-1 * (var(--vks-drawer-open-width, 75vw) + var(--vks-drawer-tab-width, 30px)))
    );
    z-index: 2;
}

.vks-section-drawer-tab:hover {
    background: var(--cfde-orange-soft, #fbeee3);
}

.vks-section-drawer-tab.is-active {
    border-color: var(--cfde-orange, #e07b39);
}

.vks-section-drawer-tab-label {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.04em;
    line-height: 1.2;
    max-height: 96px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.vks-section-drawer-panel {
    position: absolute;
    top: 0;
    right: var(--vks-drawer-tab-width, 30px);
    bottom: 0;
    pointer-events: auto;
    width: 0;
    overflow: hidden;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    transition: width 0.22s ease;
    z-index: 1;
}

.vks-section-drawer-panel.is-open {
    width: var(--vks-drawer-open-width, 75vw);
    border-left: 1px solid var(--cfde-border, #e6e1d6);
}

.vks-section-drawer-head {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--cfde-border, #e6e1d6);
}

.vks-section-drawer-title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--cfde-ink, #33363d);
}

.vks-section-drawer-close {
    border: none;
    background: transparent;
    font-size: 22px;
    line-height: 1;
    color: var(--cfde-muted, #6b6b6b);
    cursor: pointer;
    padding: 0 4px;
}

.vks-section-drawer-close:hover {
    color: var(--cfde-ink, #33363d);
}

.vks-section-drawer-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 10px 14px 16px;
    display: flex;
    flex-direction: column;
}

.vks-section-drawer-body > * {
    flex: 1 1 auto;
    min-height: 0;
}

.vks-section-drawer-note {
    margin: 0 0 10px;
    font-size: 13px;
    line-height: 1.45;
    color: var(--cfde-muted, #6b6b6b);
}
</style>
